import React from 'react';

interface AffectedTask {
  taskId: string;
  title: string;
  oldStartDate: string;
  oldDueDate: string;
  newStartDate: string;
  newDueDate: string;
}

interface GanttDragConfirmDialogProps {
  taskTitle: string;
  daysDelta: number;
  oldStartDate: string;
  oldDueDate: string;
  newStartDate: string;
  newDueDate: string;
  affectedTasks: AffectedTask[];
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const GanttDragConfirmDialog: React.FC<GanttDragConfirmDialogProps> = ({
  taskTitle,
  daysDelta,
  oldStartDate,
  oldDueDate,
  newStartDate,
  newDueDate,
  affectedTasks,
  saving,
  onConfirm,
  onCancel,
}) => {
  const direction = daysDelta > 0 ? 'later' : 'earlier';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm Reschedule</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{taskTitle}</p>
          </div>
        </div>

        {/* Date change */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Start:</span>
            <span className="text-gray-900 dark:text-white">
              {fmtDate(oldStartDate)} &rarr; {fmtDate(newStartDate)}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-500 dark:text-gray-400">Due:</span>
            <span className="text-gray-900 dark:text-white">
              {fmtDate(oldDueDate)} &rarr; {fmtDate(newDueDate)}
            </span>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Moving {Math.abs(daysDelta)} day{Math.abs(daysDelta) !== 1 ? 's' : ''} {direction}
          </div>
        </div>

        {/* Affected tasks */}
        {affectedTasks.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
              {affectedTasks.length} dependent task{affectedTasks.length !== 1 ? 's' : ''} will also be rescheduled:
            </p>
            <ul className="space-y-1.5">
              {affectedTasks.map(t => (
                <li key={t.taskId} className="text-xs text-amber-700 dark:text-amber-400">
                  <span className="font-medium">{t.title}</span>
                  <span className="text-amber-600 dark:text-amber-500 ml-1">
                    — Due: {fmtDate(t.oldDueDate)} &rarr; {fmtDate(t.newDueDate)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GanttDragConfirmDialog;
